import React from 'react';
import { P } from '@frontend/shadcn/components/typography/p';
import { TriangleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { Brief } from '../context/BriefsContext';
import { ProjectGroupItem } from './ProjectGroupItem';

interface ProjectGroup {
  projectId: number;
  projectName: string;
  briefs: Brief[];
  totalAmount: number;
}

interface ProjectState {
  fiscalYear?: string;
  isExpanded: boolean;
  periods: Array<{
    key: string;
    id: string;
    label: string;
    granularityLabelForPayload: string;
  }>;
  isSelected?: boolean;
}

interface BriefConnectionsListProps {
  projectGroups: ProjectGroup[];
  projectStates: Record<number, ProjectState>;
  onToggleProject: (projectId: number) => void;
  onUpdateProjectState: (projectId: number, updates: Partial<ProjectState>) => void;
  onToggleSelection: (projectId: number) => void;
}

export const BriefConnectionsList: React.FC<BriefConnectionsListProps> = ({
  projectGroups,
  projectStates,
  onToggleProject,
  onUpdateProjectState,
  onToggleSelection,
}) => {
  const projectsWithBudget = projectGroups.filter((group) => projectStates[group.projectId]?.periods?.length > 0);

  const projectsWithoutBudget = projectGroups.filter((group) => !projectStates[group.projectId]?.periods?.length);

  const totalBriefs = projectGroups.reduce((sum, g) => sum + g.briefs.length, 0);

  return (
    <div className="flex flex-col gap-4">
      {/* Projects with budget */}
      {projectsWithBudget.map((group) => (
        <ProjectGroupItem
          key={group.projectId}
          projectId={group.projectId}
          projectName={group.projectName}
          briefs={group.briefs}
          totalAmount={group.totalAmount}
          totalBriefs={totalBriefs}
          isExpanded={projectStates[group.projectId]?.isExpanded || false}
          onToggle={onToggleProject}
          fiscalYear={projectStates[group.projectId]?.fiscalYear}
          onUpdateState={(updates) => onUpdateProjectState(group.projectId, updates)}
          financialPeriods={projectStates[group.projectId]?.periods || []}
          isSelected={projectStates[group.projectId]?.isSelected || false}
          onToggleSelection={() => onToggleSelection(group.projectId)}
        />
      ))}

      {/* Projects without budget */}
      {projectsWithoutBudget.length > 0 && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 mt-2">
              <TriangleExclamationIcon className="w-4 h-4 text-yellow-5" />
              <P className="text-sm font-medium">No Budget Connected</P>
            </div>
            <P className="text-sm text-grey-5">
              The following projects don't have any budgets connected. Fiscal years cannot be updated for their briefs.
            </P>
          </div>
          {projectsWithoutBudget.map((group) => (
            <ProjectGroupItem
              key={group.projectId}
              projectId={group.projectId}
              projectName={group.projectName}
              briefs={group.briefs}
              totalAmount={group.totalAmount}
              totalBriefs={totalBriefs}
              isExpanded={projectStates[group.projectId]?.isExpanded || false}
              onToggle={onToggleProject}
              fiscalYear={projectStates[group.projectId]?.fiscalYear}
              onUpdateState={(updates) => onUpdateProjectState(group.projectId, updates)}
              financialPeriods={projectStates[group.projectId]?.periods || []}
              isSelected={projectStates[group.projectId]?.isSelected || false}
              onToggleSelection={() => onToggleSelection(group.projectId)}
            />
          ))}
        </>
      )}
    </div>
  );
};
